import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useActiveUsers } from '@/pages/Home/api/useActiveUsers';
import { ActiveUsersChart } from '@/pages/Home/components/ActiveUsersChart';
import { StatCell } from './components/StatCell';
import { ConfigurableMetricsRow } from './components/ConfigurableMetricsRow';
import { UserGrowthChart } from './components/UserGrowthChart';
import { useUserInsights, UserInsightsRange } from './api/useUserInsights';

function UserInsightsPage(): React.ReactElement {
  const { data: activeUsers, loading: liveLoading, error: liveError } = useActiveUsers();
  const [growthRange, setGrowthRange] = useState<UserInsightsRange>('day');
  const { data: insights, loading: insightsLoading, error: insightsError } = useUserInsights(growthRange);

  const total = activeUsers?.total ?? null;
  const consumer = activeUsers?.consumer ?? null;
  const business = activeUsers?.business ?? null;
  const fmt = (n: number | null): string => (n === null ? '—' : n.toLocaleString());

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <div className="max-w-6xl mx-auto px-6 pt-8 pb-16">

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            to="/b2b"
            className="text-sm text-gray-500 hover:text-gray-300 transition-colors no-underline"
          >
            B2B Dashboards
          </Link>
          <span className="text-gray-700 mx-2">/</span>
          <span className="text-sm text-gray-400">User Insights</span>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white font-display flex items-center gap-3">
            User Insights
            <span className="relative inline-flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
            </span>
          </h1>
          <p className="mt-2 text-sm text-gray-400">
            Real-time and historical view of users connected to the SocialSpark
            platform. Sample cadence is one minute. Counts are derived from
            active WebSocket connections, broken out by client type.
          </p>
        </div>

        {/* Live snapshot row */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <StatCell
            label="Active Now"
            info="home_active_now"
            value={liveLoading && total === null ? '…' : fmt(total)}
            accent="violet"
          />
          <StatCell
            label="Consumer"
            value={liveLoading && consumer === null ? '…' : fmt(consumer)}
            accent="emerald"
          />
          <StatCell
            label="Business"
            value={liveLoading && business === null ? '…' : fmt(business)}
            accent="pink"
          />
        </div>

        {liveError && (
          <div className="mb-6 rounded-xl border border-red-700/40 bg-red-950/40 px-4 py-3 text-sm text-red-300">
            Failed to load live count: {liveError}
          </div>
        )}

        {/* Active-users history chart (existing) */}
        <ActiveUsersChart />

        {/* Registered-users metrics row (NEW) */}
        <ConfigurableMetricsRow data={insights} className="mt-8 mb-6" />

        {/* User-growth chart (NEW) */}
        <UserGrowthChart
          data={insights}
          loading={insightsLoading}
          error={insightsError}
          range={growthRange}
          onRangeChange={setGrowthRange}
        />

      </div>
    </div>
  );
}

export default UserInsightsPage;
